import { SortErgebnisEnum } from '@comparit/shared';
import type { ErgebnisInput } from '../interfaces/ergebnis-input.model';

export class ResultsSort {
  sort<T extends ErgebnisInput>(
    results: T[],
    sortBy: SortErgebnisEnum | undefined,
    sortDescending: boolean | undefined,
    ungrouped = false
  ): T[] {
    const filtered = ungrouped ? results : results.filter(item => item.details.isMainErgebnis);
    return [...filtered].sort((a, b) => {
      let sort = 0;

      // kombinationId as the last tie-break keeps equal-value orders deterministic
      // across streaming re-sorts, page reloads and the Vergleich-PDF request.
      if (sortBy === SortErgebnisEnum.Beitrag)
        sort =
          (b.bruttoBetrag ?? 0) - (a.bruttoBetrag ?? 0) ||
          b.kombinationId.localeCompare(a.kombinationId);

      if (sortBy === SortErgebnisEnum.Versicherer)
        sort =
          b.versichererName.localeCompare(a.versichererName) ||
          (b.bruttoBetrag ?? 0) - (a.bruttoBetrag ?? 0) ||
          b.kombinationId.localeCompare(a.kombinationId);

      if (sortBy === SortErgebnisEnum.Bewertung)
        sort =
          (a.rating.wert ?? 0) - (b.rating.wert ?? 0) ||
          (b.bruttoBetrag ?? 0) - (a.bruttoBetrag ?? 0) ||
          b.kombinationId.localeCompare(a.kombinationId);

      return sortDescending ? sort : -sort;
    });
  }

  static hasAnyRating(results: ErgebnisInput[]): boolean {
    return results.some(e => e.rating.wert > 0);
  }

  static findBestTarifId(results: ErgebnisInput[]): string | null {
    const rated = results.filter(t => t.rating.wert > 0);
    if (!rated.length) return null;

    const sorted = [...rated].sort(
      (a, b) =>
        b.rating.wert - a.rating.wert ||
        (a.bruttoBetrag ?? 0) - (b.bruttoBetrag ?? 0) ||
        a.kombinationId.localeCompare(b.kombinationId)
    );
    return sorted[0].kombinationId;
  }

  /**
   * MLP recommendation sort (KFZ-2490): pins the recommendation (cheapest of
   * highest star tier) at position 0, remaining tariffs sorted by user selection.
   */
  sortWithPinnedRecommendation<T extends ErgebnisInput>(
    results: T[],
    sortBy: SortErgebnisEnum | undefined,
    sortDescending: boolean | undefined,
    ungrouped = false
  ): T[] {
    const sorted = this.sort(results, sortBy, sortDescending, ungrouped);
    const bestId = ResultsSort.findBestTarifId(sorted);

    if (!bestId) return sorted;

    const recommendation = sorted.find(r => r.kombinationId === bestId);
    const rest = sorted.filter(r => r.kombinationId !== bestId);

    return recommendation ? [recommendation, ...rest] : rest;
  }
}
